import type { CropCenterKeyframe } from '@/hooks/use-auto-save-crop-keyframes';
import { reconcileTransitions } from '@/lib/transitions/reconcileTransitions';
import { clampTransitionDuration, isOverlapKind, type SceneTransition, type KeyframeTransition } from '@/lib/transitions/types';
import { KEYFRAME_TRANSITION_EPS } from '@/lib/transitions/keyframeCuts';

export interface NaturalScene {
  id: number;
  startTime: number;
  endTime: number;
  title?: string;
}

/** A presentation-only expansion until the first scene edit commits all three
 * sets together. Reading a clip must not write migrated segments to the API. */
export function splitNaturalScenes({ intervals, boundaries, transitions, keyframeTransitions, alreadySplit = false }: {
  alreadySplit?: boolean;
  intervals: NaturalScene[];
  boundaries: number[];
  transitions: SceneTransition[];
  keyframeTransitions: KeyframeTransition[];
}) {
  if (alreadySplit) return { intervals, transitions, keyframeTransitions };
  const cuts = [...new Set(boundaries.filter(Number.isFinite))].sort((a, b) => a - b);
  let nextId = Math.max(0, ...intervals.map(i => i.id)) + 1;
  const byCut = new Map(transitions.map(t => [t.afterSceneIndex, t]));
  const promoted = new Set<KeyframeTransition>();
  const additions: SceneTransition[] = [];
  const next: NaturalScene[] = [];
  intervals.forEach((interval, index) => {
    // Keep an existing overlap's neighbors long enough that splitting cannot
    // change its half-scene cap (and therefore the output duration).
    const guard = (cut: number) => {
      const tr = byCut.get(cut);
      return tr && isOverlapKind(tr.kind) ? 2 * clampTransitionDuration(tr.durationSec) : 0.1;
    };
    let start = interval.startTime;
    let id = interval.id;
    for (const cut of cuts) {
      if (cut - start < 0.1 || cut - interval.startTime < guard(index - 1)
        || interval.endTime - cut < guard(index)) continue;
      next.push({ ...interval, id, startTime: start, endTime: cut });
      const tr = keyframeTransitions.find(t => Math.abs(t.atTime - cut) < KEYFRAME_TRANSITION_EPS);
      if (tr) {
        additions.push({ kind: tr.kind, durationSec: tr.durationSec, ...(tr.direction ? { direction: tr.direction } : {}), afterSceneIndex: next.length - 1 });
        promoted.add(tr);
      }
      start = cut;
      id = nextId++;
    }
    next.push({ ...interval, id, startTime: start });
  });
  return {
    intervals: next.length === intervals.length ? intervals : next,
    transitions: [...reconcileTransitions(intervals, next, transitions), ...additions]
      .sort((a, b) => a.afterSceneIndex - b.afterSceneIndex),
    keyframeTransitions: keyframeTransitions.filter(t => !promoted.has(t)),
  };
}

/** Resolve the insertion slot against the gesture's original layout, never
 * the shifting draft. Source ranges and IDs travel with the scene. */
export function reorderSceneAtTime<T extends { id: number }>(
  intervals: T[], id: number, time: number, centers: number[],
): T[] {
  const from = intervals.findIndex(i => i.id === id);
  if (from < 0) return intervals;
  const to = centers.filter((center, index) => index !== from && center < time).length;
  if (from === to) return intervals;
  const next = intervals.filter(i => i.id !== id);
  next.splice(to, 0, intervals[from]);
  return next;
}

/** Isolate framing at scene edges without changing the source-time crop. */
export function splitSceneCropKeyframes(
  keyframes: CropCenterKeyframe[],
  intervals: Array<{ startTime: number; endTime: number }>,
): CropCenterKeyframe[] {
  const cuts = [...new Set(intervals.flatMap(i => [i.startTime, i.endTime]))]
    .filter(Number.isFinite).sort((a, b) => a - b);
  return keyframes.flatMap(keyframe => {
    const edges = [keyframe.time, ...cuts.filter(t => t > keyframe.time && t < keyframe.endTime), keyframe.endTime];
    return edges.slice(1).map((endTime, i) => ({ ...keyframe, time: edges[i], endTime }));
  });
}
